import { useEffect, useMemo, useState } from "react";
import { useBoxBuilder } from "@/lib/store";
import { calculateBreakdown, countSelectedItems } from "@/lib/pricing";
import {
  fetchBoxItems,
  fetchBoxTiers,
  fetchPremiumItems,
  fetchTissuePapers,
} from "@/lib/api";
import type {
  BoxItem,
  BoxTier,
  PremiumItem,
  TissuePaper,
} from "@/lib/types";
import TierSelector from "./TierSelector";
import ItemGrid from "./ItemGrid";
import PremiumAddOns from "./PremiumAddOns";
import TissueSelector from "./TissueSelector";
import SummaryBar from "./SummaryBar";
import SectionHeader from "./SectionHeader";
import LoadingState from "./LoadingState";
import ErrorState from "./ErrorState";

interface Catalog {
  tiers: BoxTier[];
  items: BoxItem[];
  premium: PremiumItem[];
  tissue: TissuePaper[];
}

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; data: Catalog };

export default function BoxBuilder() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tiers, items, premium, tissue] = await Promise.all([
          fetchBoxTiers(),
          fetchBoxItems(),
          fetchPremiumItems(),
          fetchTissuePapers(),
        ]);
        if (cancelled) return;
        setState({ status: "ready", data: { tiers, items, premium, tissue } });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          message:
            err instanceof Error
              ? err.message
              : "Couldn't load the catalog right now.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return <LoadingState />;
  }
  if (state.status === "error") {
    return (
      <ErrorState
        message={state.message}
        onRetry={() => setState({ status: "loading" })}
      />
    );
  }

  return <Ready catalog={state.data} />;
}

function Ready({ catalog }: { catalog: Catalog }) {
  const { tiers, items, premium, tissue } = catalog;
  const tierVariationId = useBoxBuilder((s) => s.tierVariationId);
  const selectedItems = useBoxBuilder((s) => s.selectedItems);
  const selectedPremium = useBoxBuilder((s) => s.selectedPremium);
  const tissueId = useBoxBuilder((s) => s.tissueId);
  const setTier = useBoxBuilder((s) => s.setTier);
  const setTissue = useBoxBuilder((s) => s.setTissue);

  const activeTier = useMemo(
    () => tiers.find((t) => t.variationId === tierVariationId),
    [tiers, tierVariationId],
  );

  // On first mount, pick the smallest tier automatically.
  useEffect(() => {
    if (!activeTier && tiers.length > 0) {
      setTier(tiers[0].variationId);
    }
  }, [activeTier, tiers, setTier]);

  // If there's only one tissue option, pre-select it.
  useEffect(() => {
    if (tissueId === null && tissue.length === 1) {
      setTissue(tissue[0].id);
    }
  }, [tissueId, tissue, setTissue]);

  const itemLimit = activeTier?.itemCount ?? 0;
  const selectedCount = countSelectedItems(selectedItems);
  const breakdown = calculateBreakdown(activeTier, selectedPremium, premium);

  const missing: string[] = [];
  if (tiers.length === 0) missing.push("box tiers");
  if (items.length === 0) missing.push("box items");
  if (tissue.length === 0) missing.push("tissue paper options");
  if (missing.length > 0) {
    return (
      <div className="rounded-2xl bg-white p-8 ring-1 ring-ink-100">
        <h2 className="text-2xl font-semibold text-ink-800">
          The box builder isn&apos;t ready yet
        </h2>
        <p className="mt-2 text-ink-500">
          An admin still needs to set up {missing.join(", ")} in WordPress
          before shoppers can build a box.
        </p>
      </div>
    );
  }

  return (
    <div className="pb-28 sm:pb-24">
      <section>
        <SectionHeader
          step="1"
          title="Choose your box size"
          description="Each size holds a set number of items and ships anywhere in the country."
        />
        <TierSelector tiers={tiers} />
      </section>

      <section className="mt-12">
        <SectionHeader
          step="2"
          title="Fill your box"
          description={
            itemLimit > 0
              ? `Pick exactly ${itemLimit} item${itemLimit === 1 ? "" : "s"}. You can choose more than one of something.`
              : "Pick your items."
          }
          rightSlot={
            itemLimit > 0 ? (
              <span
                className={
                  selectedCount === itemLimit
                    ? "rounded-full bg-brand-500 px-3.5 py-1.5 text-sm font-semibold uppercase tracking-[0.14em] text-white"
                    : "rounded-full bg-brand-100 px-3.5 py-1.5 text-sm font-semibold uppercase tracking-[0.14em] text-brand-700"
                }
              >
                {selectedCount} / {itemLimit} picked
              </span>
            ) : null
          }
        />
        <ItemGrid items={items} limit={itemLimit} />
      </section>

      {premium.length > 0 ? (
        <section className="mt-12">
          <SectionHeader
            step="3"
            title="Add a premium upgrade (optional)"
            description="Level up the box with a limited-edition local favorite. Each adds to the total."
          />
          <PremiumAddOns items={premium} />
        </section>
      ) : null}

      <section className="mt-12">
        <SectionHeader
          step={premium.length > 0 ? "4" : "3"}
          title="Pick your tissue paper"
          description="Tissue lines the box to keep everything snug. Required."
        />
        <TissueSelector options={tissue} />
      </section>

      <SummaryBar
        tier={activeTier}
        itemLimit={itemLimit}
        selectedCount={selectedCount}
        tissueSelected={tissueId !== null}
        breakdown={breakdown}
      />
    </div>
  );
}
